import { QuoteData } from "@/app/common-types/quote/quote-data";
import { RenewalPage } from "@/config/app-constant";
import {
  APPLICATION,
  BASIC,
  COMPREHENSIVE,
  INT_COUNTRY,
  SECONDARY_PRODUCT,
} from "@/constants";
import { DynamicCoverLimit } from "@/types/DynamicCoverLimitType";
import GTMData from "@/types/gtm/QuoteGtmEvent";
import { OptionalExtra } from "@/types/OptionalExtraType";
import { Attributes } from "@/types/quote/quote-data";

import { calculateAgeFromDates } from "../travel-date.utils";

type PolicyPackage =
  | "BASIC"
  | "COMPREHENSIVE"
  | "SIGNATURE"
  | "ESSENTIALS"
  | "CLASSIC"
  | "DELUXE";

export function getCoverForCancellation(
  coverLimits: DynamicCoverLimit[] | null,
  // policyType: string | undefined,
  policyType: string | undefined,
  policyPackage: string
): string | null {
  if (policyPackage === COMPREHENSIVE) return "£5,000";
  const products = coverLimits?.[0].product;
  const coverLimit = products?.find(
    (product) =>
      product.policyType === policyType &&
      product.policyPackage === policyPackage
  );

  if (coverLimit) {
    const selectedValue = coverLimit?.values.find(
      (value) => value.selected === true
    );
    if (selectedValue) {
      return selectedValue.displayValue;
    }
    return null;
  }
  return null;
}

export function getOptionExtraSellPrice(
  optionExtraId: string,
  optionExtras: OptionalExtra[]
): string | null {
  const optionExtra = optionExtras.find(
    (option) => option.code === optionExtraId
  );

  if (!optionExtra) return null; // Return empty string if optionExtra is not found

  const { price } = optionExtra;

  if (!price?.length) return null; // Return null if price is not defined or empty

  const { state, selected } = price[0];

  if (state === "DISABLED") return null;
  if (state === "INCLUDED") return "true";
  if (selected) return "true";
  if (!selected) return "false";

  return null; // Return empty string for all other conditions
}

export function getCoverBaggage(
  policyPackage: PolicyPackage,
  goingOnCruise: boolean
) {
  const POLICY_AMOUNTS: Record<
    PolicyPackage,
    string | { default: string; cruise: string }
  > = {
    BASIC: "£300",
    COMPREHENSIVE: "£2,500",
    ESSENTIALS: "£1,000",
    CLASSIC: {
      default: "£2,000",
      cruise: "£2,500",
    },
    DELUXE: {
      default: "£3,000",
      cruise: "£5,000",
    },
    SIGNATURE: "£5,000",
  };

  const amount = POLICY_AMOUNTS[policyPackage];
  if (typeof amount === "string") {
    return amount;
  }
  return goingOnCruise ? amount.cruise : amount.default;
}

export function getExcess(policyPackage: PolicyPackage) {
  const excessValues: Record<PolicyPackage, string> = {
    BASIC: "£129",
    COMPREHENSIVE: "£99",
    SIGNATURE: "£0",
    ESSENTIALS: "£150",
    CLASSIC: "£75",
    DELUXE: "£0",
  };

  return excessValues[policyPackage] || "£0";
}

export function getMedicalCoverAmount(
  initalLoad: boolean,
  policyPackage: string
) {
  if (initalLoad) {
    return null;
  }

  const medicalCoverData: Record<PolicyPackage, { cover: string }> = {
    BASIC: {
      cover: "£5m",
    },
    COMPREHENSIVE: {
      cover: "unlimited",
    },
    SIGNATURE: {
      cover: "unlimited",
    },
    ESSENTIALS: {
      cover: "£5m",
    },
    CLASSIC: {
      cover: "£10m",
    },
    DELUXE: {
      cover: "unlimited",
    },
  };

  return medicalCoverData[policyPackage as PolicyPackage].cover;
}

export function getLegalProtection(initalLoad: boolean, policyPackage: string) {
  if (initalLoad) {
    return null;
  }
  return policyPackage === BASIC ? "false" : "true";
}

export function getRepatriation(
  initalLoad: boolean,
  policyPackage: PolicyPackage
) {
  if (initalLoad) {
    return null;
  }

  const repatriationValues: Record<PolicyPackage, string> = {
    BASIC: "£5m",
    COMPREHENSIVE: "unlimited",
    SIGNATURE: "unlimited",
    ESSENTIALS: "£5m",
    CLASSIC: "£10m",
    DELUXE: "unlimited",
  };

  return repatriationValues[policyPackage];
}

function getFormattedDate(date: string | undefined): string | null {
  return date ? date.split("-").join("/") : null;
}

function getOptionalExtrasData(
  initalLoad: boolean,
  optionalExtras: OptionalExtra[]
) {
  if (initalLoad) {
    return {
      personalLiability: null,
      winterSports: null,
      travelDisruption: null,
      excessWaiver: null,
      gadgetCover: null,
      golfCover: null,
      carHireExcessWaiver: null,
      cruiseCover: null,
      hundredDaysCover: null,
    };
  }

  return {
    personalLiability: getOptionExtraSellPrice(
      "PERSONAL_LIABILITY_COVER",
      optionalExtras
    ),
    winterSports: getOptionExtraSellPrice(
      "WINTER_SPORTS_COVER",
      optionalExtras
    ),
    travelDisruption: getOptionExtraSellPrice("TDE", optionalExtras),
    excessWaiver: getOptionExtraSellPrice("EXCESS_WAIVER", optionalExtras),
    gadgetCover: getOptionExtraSellPrice("GADGET_COVER", optionalExtras),
    golfCover: getOptionExtraSellPrice("GOLF_EQUIPMENT", optionalExtras),
    carHireExcessWaiver: getOptionExtraSellPrice(
      "CAR_HIRE_EXCESS_WAIVER",
      optionalExtras
    ),
    cruiseCover: getOptionExtraSellPrice("CRUISE_COVER", optionalExtras),
    hundredDaysCover: getOptionExtraSellPrice(
      "HUNDRED_DAYS_DURATION",
      optionalExtras
    ),
    singleItemCover: initalLoad
      ? null
      : getOptionExtraSellPrice("SINGLE_ITEM_COVER", optionalExtras),
  };
}

function getCoverageAmounts(
  initalLoad: boolean,
  policyType: string,
  policyPackage: string,
  dynamicCoverLimit: DynamicCoverLimit[] | undefined | null,
  goingOnCruise: boolean
) {
  if (initalLoad) {
    return {
      medical: null,
      cancellation: null,
      baggage: null,
      excess: null,
    };
  }

  return {
    medical: getMedicalCoverAmount(initalLoad, policyPackage),
    cancellation: getCoverForCancellation(
      dynamicCoverLimit ?? null,
      policyType,
      policyPackage
    ),
    baggage: getCoverBaggage(policyPackage as PolicyPackage, goingOnCruise),
    excess: getExcess(policyPackage as PolicyPackage),
  };
}

function hasMedicalConditions(
  travellerDetails: QuoteData["attributes"]["travellerDetails"]
) {
  return travellerDetails?.traveller?.some((traveller) => traveller.medical)
    ? "true"
    : "false";
}

function QuoteDataMap(
  attributes: Attributes,
  eventName?: string,
  stepName?: string,
  referenceId?: string,
  event?: string
): Partial<GTMData> {
  const {
    tripDetails,
    priceResult,
    travellerDetails,
    organiser,
    paymentDetails,
    gtmEvents,
  } = attributes;

  if (!tripDetails || !priceResult) {
    return {};
  }

  const {
    policyType,
    policyPackage = "",
    initialQuotePageLoading: initalLoad = false,
    goingOnCruise = false,
    startDate,
    policyEndDate,
    toLocation,
    area,
    fromLocation,
    partyType,
  } = tripDetails;

  const { dynamicCoverLimit, optionalExtras } = priceResult;
  const { firstName, lastName, email, telephone, dateOfBirth } =
    organiser ?? {};

  const travellerAge = calculateAgeFromDates(dateOfBirth ?? "")?.toString();
  const paymentPrice =
    attributes.paymentDetails?.paymentOptions[0].cardPayment.card.price;

  const coverageAmounts = getCoverageAmounts(
    initalLoad,
    policyType ?? "",
    policyPackage ?? "",
    dynamicCoverLimit,
    goingOnCruise
  );

  const optionalExtrasData = getOptionalExtrasData(initalLoad, optionalExtras);

  const baseData = {
    event: event ?? "ecom_events",
    event_name: eventName ?? "view_item_list",
    step_name: stepName ?? "order confirmation",
    first_name: firstName,
    last_name: lastName,
    email_address: email,
    phone_number: telephone,
    age: travellerAge,
    trip_type: policyType,
    cover_start_date: getFormattedDate(startDate),
    cover_end_date: getFormattedDate(policyEndDate),
    travel_from: fromLocation ?? null,
    travel_group_type: partyType?.toLowerCase() ?? null,
    amount_of_cover_medical: coverageAmounts.medical,
    amount_of_cover_cancellation: coverageAmounts.cancellation,
    amount_of_cover_baggage: coverageAmounts.baggage,
    excess: coverageAmounts.excess,
    covid_cover: "true",
    legal_protection: getLegalProtection(initalLoad, policyPackage),
    repatriation: getRepatriation(initalLoad, policyPackage as PolicyPackage),
    personal_liability: optionalExtrasData.personalLiability,
    winter_sports: optionalExtrasData.winterSports,
    travel_disruption: optionalExtrasData.travelDisruption,
    excess_waiver: optionalExtrasData.excessWaiver,
    gadget_cover: optionalExtrasData.gadgetCover,
    golf_cover: optionalExtrasData.golfCover,
    car_hire_excess_waiver: optionalExtrasData.carHireExcessWaiver,
    quote_id: referenceId,
    medical_condition_added: hasMedicalConditions(travellerDetails),
  };

  const quoteUniqueData = {
    destination: toLocation ?? null,
    destination_additions: area ?? null,
    trip_extra: goingOnCruise ? "true" : "false",
    cruise_cover: optionalExtrasData.cruiseCover,
    hundred_days_cover: optionalExtrasData.hundredDaysCover,
    ecommerce: {
      currency: "GBP",
      value: paymentPrice ? parseFloat(paymentPrice) : null,
    },
  };

  const renewalUniqueData = {
    previous_policy_id: gtmEvents?.previousPolicyId ?? "N/A",
    renewal_price: paymentDetails?.paymentOptions[0]?.cardPayment?.card?.price
      ? Number(paymentDetails.paymentOptions[0].cardPayment.card.price)
      : null,
    last_year_price: priceResult?.renewalPolicyPackage?.lastYearPrice,
    cover_type: tripDetails.policyPackage,
  };

  const expatQuoteData = {
    single_item_cover: optionalExtrasData.singleItemCover,
  };

  const internationalQuoteData = {
    private_health_insurance: travellerDetails?.traveller?.[0]?.hasPMI,
    natilality: organiser?.nationalityId,
  };

  if (APPLICATION === RenewalPage) {
    return { ...baseData, ...renewalUniqueData };
  }

  if (INT_COUNTRY === "ireland") {
    return { ...baseData, ...quoteUniqueData, ...internationalQuoteData };
  }

  if (SECONDARY_PRODUCT === "expat") {
    return { ...baseData, ...quoteUniqueData, ...expatQuoteData };
  }

  return { ...baseData, ...quoteUniqueData };
}

export default QuoteDataMap;
